import React, { useState } from 'react';
import Sidebar from '../components/Sidebar';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../context/AuthContext';
import { submitFeedback } from '../api/feedback';
import FileDropZone from '../components/FileDropZone';
import './Page.css';
import './SubmitFeedback.css';

const SubmitFeedback: React.FC = () => {
  const { user } = useAuth();
  const [kind, setKind] = useState<'bug' | 'request'>('bug');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  if (!user) return null;

  const handleFilesChosen = (chosen: File[]) => {
    if (!chosen.length) {
      setFiles([]);
      return;
    }
    setFiles(chosen);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    if (!subject.trim()) {
      setMessage({ type: 'error', text: 'Please enter a subject.' });
      return;
    }
    if (!description.trim()) {
      setMessage({ type: 'error', text: 'Please describe the bug or request.' });
      return;
    }
    setSubmitting(true);
    try {
      await submitFeedback({
        userId: user.id,
        kind,
        subject: subject.trim(),
        description: description.trim(),
        files,
      });
      setMessage({ type: 'success', text: 'Your feedback was sent. Thank you!' });
      setSubject('');
      setDescription('');
      setFiles([]);
      const input = document.getElementById('feedback-files') as HTMLInputElement | null;
      if (input) input.value = '';
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Could not send feedback.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-layout">
        <Sidebar />
        <main className="page-main submit-feedback-layout">
          <div className="submit-feedback-column">
            <PageHeader title="Submit Feedback" subtitle="Report a bug or suggest an improvement." />

            <div className="page-content submit-feedback-page">
            {message && (
              <div
                className={`submit-feedback-message submit-feedback-message--${message.type}`}
                role="alert"
              >
                {message.text}
              </div>
            )}

            <form className="submit-feedback-form" onSubmit={handleSubmit}>
              <div className="submit-feedback-section">
                <h3 className="submit-feedback-section-title">Type</h3>
                <div className="submit-feedback-radio-box">
                  <div className="submit-feedback-radio-row">
                    <label className="submit-feedback-radio-label">
                      <input
                        type="radio"
                        name="feedback-kind"
                        value="bug"
                        checked={kind === 'bug'}
                        onChange={() => setKind('bug')}
                      />
                      Bug
                    </label>
                    <label className="submit-feedback-radio-label">
                      <input
                        type="radio"
                        name="feedback-kind"
                        value="request"
                        checked={kind === 'request'}
                        onChange={() => setKind('request')}
                      />
                      Request
                    </label>
                  </div>
                </div>
              </div>

              <label className="submit-feedback-label">
                <span className="submit-feedback-label-text">Subject</span>
                <input
                  type="text"
                  className="submit-feedback-input"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Short summary"
                  maxLength={200}
                  autoComplete="off"
                />
              </label>

              <label className="submit-feedback-label">
                <span className="submit-feedback-label-text">Description</span>
                <textarea
                  className="submit-feedback-textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what happened, what you expected, and steps to reproduce (for bugs) or the change you’d like (for requests)."
                  rows={10}
                />
              </label>

              <div className="submit-feedback-attachments">
                <div className="submit-feedback-label">
                  <span className="submit-feedback-label-text">Attachments (optional)</span>
                  <span className="submit-feedback-hint"> Images or videos only — up to 10 files, 45 MB each.</span>
                </div>
                <FileDropZone
                  id="feedback-files"
                  accept="image/*,video/*"
                  multiple
                  hint="Drag files here or browse — replaces the current selection."
                  onFiles={handleFilesChosen}
                  className="submit-feedback-file-drop"
                />
                {files.length > 0 && (
                  <ul className="submit-feedback-file-list">
                    {files.map((f) => (
                      <li key={`${f.name}-${f.size}-${f.lastModified}`}>
                        {f.name} <span className="submit-feedback-file-type">({f.type || 'unknown type'})</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="submit-feedback-actions">
                <button type="submit" className="save-button" disabled={submitting}>
                  {submitting ? 'Sending…' : 'Submit'}
                </button>
              </div>
            </form>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default SubmitFeedback;
